import { useState } from "react";
import { useApi, apiCall } from "../../api/client";
import {
  sessions as sessionsContract,
  type SessionClaimGroup,
  type SessionClaimRow,
  type SessionClaimSliceResponse,
} from "../../api/contracts/sessions";
import { Button } from "../../button";
import { Card } from "../../card";
import { Skeleton } from "../../skeleton";
import { IntentBadge } from "../status-badge";
import { PeekDialogSection } from "../peek-dialog";
import { formatDateTime } from "../../format";

/**
 * Claims-on-review panel (P2-U2, verification-loop Wave B-UI).
 *
 * The task peek's view of what the P2-01 done gate evaluates: every
 * `claim.assert` on the task's live sessions (linked via `origin_task_id`),
 * each expandable to the trajectory slice that supports it — the HTTP twin of
 * the MCP `session_claim_slice` verifier surface. Read-only; asserting a claim
 * stays an MCP/core write (`session_claim`), never a UI path.
 *
 * Renders NOTHING when the task has no live sessions (the installed base —
 * the gate is default-inert there). Sessions with a trajectory but no claims
 * render an explicit "none asserted" line, because "no claims" on gated work
 * is a fact the verifier acts on, not an empty state to hide.
 */
export function SessionClaimsPanel({ taskId }: { taskId: number }) {
  const { data, isLoading } = useApi(sessionsContract.claims, {
    query: { task_id: taskId },
  });

  if (isLoading && !data) {
    return (
      <PeekDialogSection title="Claims on review">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </PeekDialogSection>
    );
  }

  if (!data || data.sessions.length === 0) return null;

  const withClaims = data.sessions.filter((s) => s.claims.length > 0);
  const total = withClaims.reduce((n, s) => n + s.claims.length, 0);

  return (
    <PeekDialogSection title="Claims on review" count={total}>
      {total === 0 ? (
        <div className="type-body text-muted-foreground">
          No claim.assert on this task&apos;s {data.sessions.length} live
          session{data.sessions.length === 1 ? "" : "s"} yet. A verified done on
          claim-gated work needs a bound claim or a reasoned waiver.
        </div>
      ) : (
        withClaims.map((s) => <SessionClaimsCard key={s.id} session={s} />)
      )}
    </PeekDialogSection>
  );
}

function SessionClaimsCard({ session }: { session: SessionClaimGroup }) {
  return (
    <Card className="px-4 py-2">
      <div className="flex flex-wrap items-center gap-2 border-b border-border/40 py-2 type-body">
        <span className="flex-1 text-foreground">
          {session.title ?? session.urn}
        </span>
        <IntentBadge intent="neutral">{session.kind}</IntentBadge>
        <span className="text-muted-foreground">{session.urn}</span>
      </div>
      {session.claims.map((claim) => (
        <ClaimRow key={claim.seq} urn={session.urn} claim={claim} />
      ))}
    </Card>
  );
}

function predicateText(predicate: unknown): string {
  if (typeof predicate === "string") return predicate;
  try {
    return JSON.stringify(predicate);
  } catch {
    return String(predicate);
  }
}

function ClaimRow({ urn, claim }: { urn: string; claim: SessionClaimRow }) {
  const [open, setOpen] = useState(false);
  const [slice, setSlice] = useState<SessionClaimSliceResponse | null>(null);
  const [loading, setLoading] = useState(false);

  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (slice || loading) return;
    setLoading(true);
    try {
      const res = await apiCall(sessionsContract.claimSlice, {
        query: { urn, claim_seq: claim.seq },
      });
      setSlice(res);
    } catch (e) {
      setSlice({ ok: false, reason: (e as Error).message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border-b border-border/40 py-2 type-body last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex-1 break-words text-foreground">
          {predicateText(claim.predicate)}
        </span>
        <IntentBadge intent="info">claim #{claim.seq}</IntentBadge>
        <IntentBadge intent="neutral">
          slice {claim.fromSeq}..{claim.toSeq}
        </IntentBadge>
        <Button type="button" variant="ghost" size="xs" onClick={toggle}>
          {open ? "Hide slice" : "Show slice"}
        </Button>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 text-muted-foreground">
        <span>{claim.actorId}</span>
        <span>{formatDateTime(claim.createdAt)}</span>
      </div>
      {open ? <SliceView slice={slice} loading={loading} /> : null}
    </div>
  );
}

function payloadExcerpt(payload: unknown): string {
  const text = predicateText(payload);
  return text.length > 200 ? `${text.slice(0, 200)}…` : text;
}

function SliceView({
  slice,
  loading,
}: {
  slice: SessionClaimSliceResponse | null;
  loading: boolean;
}) {
  if (loading || !slice) {
    return <Skeleton className="mt-2 h-4 w-full" />;
  }

  if (!slice.ok) {
    return (
      <div className="mt-2 text-muted-foreground">
        slice unavailable: {slice.reason}
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-1">
      {slice.slice.kind === "missing" ? (
        <div className="text-muted-foreground">
          slice missing: {slice.slice.reason}
        </div>
      ) : slice.slice.kind === "hash-only" ? (
        <>
          <IntentBadge intent="warning">payload tombstoned — hash-only</IntentBadge>
          {slice.slice.hashes.map((h) => (
            <div key={h.seq} className="flex flex-wrap gap-x-2 text-muted-foreground">
              <span className="text-foreground">#{h.seq}</span>
              <span>{h.kind}</span>
              <span className="break-all">{h.payloadHash ?? "—"}</span>
            </div>
          ))}
        </>
      ) : (
        slice.slice.actions.map((a) => (
          <div key={a.seq} className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-foreground">#{a.seq}</span>
            <IntentBadge intent="neutral">{a.kind}</IntentBadge>
            <span className="break-words text-muted-foreground">
              {payloadExcerpt(a.payload)}
            </span>
          </div>
        ))
      )}
      <div className="text-muted-foreground">{slice.citation}</div>
    </div>
  );
}
